import { getNumberFromQueryString } from '@if/core/utils'
import { Box, Button, FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, Stack, Tab, Tabs } from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import { enUS, frFR } from '@mui/x-data-grid/locales'
import ConfirmActionButton from '@src/shared/components/ConfirmActionButton'
import {
  useApplicationNames,
  useBugPlateformeValues,
  useBugSeverityValues,
  useBulkCloseExceptions,
  useExceptionsList,
} from '@src/tools/error-tracker/api/exceptions-api'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { EExceptionStatus } from '@src/tools/error-tracker/types/Constants'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { ExceptionsFiltersDto } from '@src/tools/error-tracker/types/ExceptionsFiltersDto'
import { useToast } from '@src/shared/hooks/useToast'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ActionsArea from './Components/ActionsArea'
import AddTicketDialog from './Components/AddTicketDialog'
import BulkAssignTicketDialog from './Components/BulkAssignTicketDialog'
import BulkIgnoreDialog from './Components/BulkIgnoreDialog'
import EditExceptionDialog from './Components/EditExceptionDialog'
import { useExceptionStatuses } from './Components/EditExceptionDialog-hooks'
import ExceptionsFiltersDialog from './Components/ExceptionsFiltersDialog'
import NewTicketDialog from './Components/NewTicketDialog'
import Footer from './Components/footer'
import Header from './Components/header'
import { useExceptionsColumns, useSearchQuery } from './browseExceptions-hooks'
import { getStartDate } from './browseExceptions-selectors'

const BrowseExceptionsPage = () => {
  const { i18n, t } = useTranslation()
  const [searchParams] = useSearchParams()
  const updateSearch = new URLSearchParams(location.search)
  const navigate = useNavigate()


  const query = useSearchQuery(searchParams)

  const [exceptions, isFetching] = useExceptionsList(query)

  const [applicationNames, isFetchingAppNames] = useApplicationNames()

  const [bulkCloseExceptions, isBulkCloseLoading] = useBulkCloseExceptions()

  const [severityValues] = useBugSeverityValues()
  const [plateformeValues] = useBugPlateformeValues()
  const { showSuccess, ToastComponent } = useToast()

  // update search
  const [openFilter, setOpenFilter] = useState(false)
  
  const getTabFromStatus = (status: EExceptionStatus): number => {
    switch (status) {
      case EExceptionStatus.New: return 0
      case EExceptionStatus.TicketAssigned: return 1
      case EExceptionStatus.Closed: return 2
      case EExceptionStatus.Ignored: return 3
      default: return 0
    }
  }
  
  const [selectedTab, setSelectedTab] = useState(() => 
    query.statuses.length > 0 ? getTabFromStatus(query.statuses[0] as EExceptionStatus) : 0
  )

  // Sync tab with URL when query changes
  useEffect(() => {
    if (query.statuses.length > 0) {
      const newTab = getTabFromStatus(query.statuses[0] as EExceptionStatus)
      if (newTab !== selectedTab) {
        setSelectedTab(newTab)
      }
    }
  }, [query.statuses, selectedTab])

  const updateUriQuery = () => {
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: false })
  }

  const setPagination = (pagination: GridPaginationModel) => {
    updateSearch.set('offset', encodeURIComponent(query.limit * pagination.page))
    updateSearch.set('limit', encodeURIComponent(pagination.pageSize))
    updateUriQuery()
  }

  const pagination = {
    pageSize: getNumberFromQueryString(searchParams, 'limit', 25),
    page: getNumberFromQueryString(searchParams, 'offset', 0) / getNumberFromQueryString(searchParams, 'limit', 25),
  }

  const [openDialog, setOpenDialog] = useState(false)
  const [openAddTicketDialog, setOpenAddTicketDialog] = useState(false)
  const [openBulkIgnoreDialog, setOpenBulkIgnoreDialog] = useState(false)
  const [openBulkAssignTicketDialog, setOpenBulkAssignTicketDialog] = useState(false)
  const [openNewTicketDialog, setOpenNewTicketDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState<ExceptionLog | null>(null)
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([])

  // Convert selectedIds to string array for easier handling
  const selectedIdsArray = selectedIds as string[]

  // Get selected exceptions for dialog
  const selectedExceptions = exceptions.filter(exception => selectedIdsArray.includes(exception.id))

  const exceptionStatuses = useExceptionStatuses()
  
  const handleDialogSuccess = useCallback(() => {
    setOpenDialog(false)
    setOpenAddTicketDialog(false)
    setOpenBulkIgnoreDialog(false)
    setOpenBulkAssignTicketDialog(false)
    setOpenNewTicketDialog(false)
    setSelectedRow(null)
    setSelectedIds([]) // Clear selections after successful operations
  }, [])

  const handleBulkIgnoreSuccess = useCallback(() => {
    setOpenBulkIgnoreDialog(false)
    setSelectedIds([]) // Clear selections after successful bulk ignore
  }, [])

  const handleBulkAssignTicketSuccess = useCallback(() => {
    setOpenBulkAssignTicketDialog(false)
    setSelectedIds([]) // Clear selections after successful bulk assign ticket
  }, [])

  const DevopsBaseUrl = 'https://dev.azure.com/IcebergFinance/IcebergFinance/_workitems/edit/'

  const handleNewTicketSuccess = useCallback((ticketNumber: string) => {
    setOpenNewTicketDialog(false)
    setSelectedIds([]) // Clear selections after successful new ticket creation
    if (ticketNumber) {
      showSuccess(
        <>
          {t('dialogs.newTicket.success', 'Ticket created successfully!')}
          {' '}
          <Link href={`${DevopsBaseUrl}${ticketNumber}`} target="_blank" rel="noopener" sx={{ color: 'inherit', fontWeight: 'bold' }}>
            #{ticketNumber}
          </Link>
        </>
      )
    }
  }, [showSuccess, t])

  const handleOpenDialog = useCallback((params: GridRenderCellParams<ExceptionLog>) => {
    setSelectedRow(params.row)
    setOpenDialog(true)
  }, [])

  const handleOpenAddTicketDialog = useCallback((params: GridRenderCellParams<ExceptionLog>) => {
    setSelectedRow(params.row)
    setOpenAddTicketDialog(true)
  }, [])

  const handleSearch = (value: string) => {
    updateSearch.set('searchValue', encodeURIComponent(value))
    updateUriQuery()
  }

  const handleFilters = (filters: ExceptionsFiltersDto) => {
    updateSearch.set('applicationName', encodeURIComponent(filters.applicationName))
    updateSearch.set('startDate', encodeURIComponent(filters.startDate))

    updateUriQuery()
    setOpenFilter(false)
  }

  const handleClearFilter = () => {
    handleFilters({
      applicationName: '',
      startDate: getStartDate(),
    } as ExceptionsFiltersDto)
  }

  const hasFilter = (): boolean => {
    return query.applicationName !== '' || query.startDate !== getStartDate()
  }

  const getTabStatus = (tabIndex: number): EExceptionStatus => {
    switch (tabIndex) {
      case 0: return EExceptionStatus.New
      case 1: return EExceptionStatus.TicketAssigned
      case 2: return EExceptionStatus.Closed
      case 3: return EExceptionStatus.Ignored
      default: return EExceptionStatus.New
    }
  }

  const handleSortChange = (event: SelectChangeEvent) => {
    updateSearch.set('sortBy', encodeURIComponent(event.target.value))
    updateSearch.set('offset', '0')
    updateUriQuery()
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    updateSearch.set('statuses', encodeURIComponent(getTabStatus(newValue)))
    updateUriQuery()
    // Clear selections when switching tabs
    setSelectedIds([])
  }

  const handleRowSelectionModelChange = useCallback((newSelection: GridRowSelectionModel) => {
    setSelectedIds(newSelection)
  }, [])

  const handleIgnoreClick = useCallback(() => {
    setOpenBulkIgnoreDialog(true)
  }, [])

  const handleAssignTicketClick = useCallback(() => {
    setOpenBulkAssignTicketDialog(true)
  }, [])

  const handleNewTicketClick = useCallback(() => {
    setOpenNewTicketDialog(true)
  }, [])

  const handleCloseClick = useCallback(async () => {
    if (selectedIdsArray.length === 0) return
    
    try {
      await bulkCloseExceptions({
        exceptionIds: selectedIdsArray,
        note: 'Bulk closed from ErrorTracker'
      })
      setSelectedIds([]) // Clear selections after successful close
    } catch (error) {
      reportErrorToConsole(error)
    }
  }, [selectedIdsArray, bulkCloseExceptions])

  // Table def
  const shouldShowTicketColumn = selectedTab === 1 || selectedTab === 2 // In Progress or Resolved
  const columns: GridColDef[] = useExceptionsColumns(handleOpenDialog, handleOpenAddTicketDialog, shouldShowTicketColumn)

  const localeTextDatagrid =
    i18n.language === 'fr'
      ? frFR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText

  return (
    <>
      <Box sx={{ mt: 2, mb: 1 }}>
        <Stack sx={{ mx: '15px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '1rem',
            }}
          >
            <Header
              handleSearch={handleSearch}
              onOpenFilterClicked={() => setOpenFilter(true)}
              handleClearFilter={handleClearFilter}
              hasFilter={hasFilter()}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="sort-select-label">{t('filters.sortBy', 'Sort by')}</InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={query.sortBy}
                  label={t('filters.sortBy', 'Sort by')}
                  onChange={handleSortChange}
                >
                  <MenuItem value="LastDetectedOn">{t('filters.sortLastDetected', 'Last detected (desc)')}</MenuItem>
                  <MenuItem value="FirstTimeGenerated">{t('filters.sortFirstDetected', 'First detected (desc)')}</MenuItem>
                  <MenuItem value="Count">{t('filters.sortCount', 'Occurrences count (desc)')}</MenuItem>
                </Select>
              </FormControl>
            </Header>
          </Box>
          <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={t('exceptionStatus.new', 'New')} />
            <Tab label={t('exceptionStatus.ticketAssigned', 'In Progress')} />
            <Tab label={t('exceptionStatus.closed', 'Resolved')} />
            <Tab label={t('exceptionStatus.ignored', 'Ignored')} />
          </Tabs>
          {selectedTab <= 1 && ( // Only show ActionsArea in New tab
            <ActionsArea selectedCount={selectedIdsArray.length}>
              <Button
                variant="contained"
                color="success"
                onClick={handleNewTicketClick}
                disabled={selectedIdsArray.length === 0}
                size="small"
              >
                {t('actions.newTicket', 'New Ticket')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssignTicketClick}
                disabled={selectedIdsArray.length === 0}
                size="small"
              >
                {t('actions.assignTicket', 'Existing Ticket')}
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleIgnoreClick}
                disabled={selectedIdsArray.length === 0}
                size="small"
              >
                {t('actions.ignore', 'Ignore')}
              </Button>
              <ConfirmActionButton
                variant="contained"
                color="warning"
                onConfirm={handleCloseClick}
                disabled={selectedIdsArray.length === 0 || isBulkCloseLoading}
                size="small"
              >
                {isBulkCloseLoading ? 'Closing...' : t('actions.close', 'Close')}
              </ConfirmActionButton>
            </ActionsArea>
          )}
        </Stack>

        <DataGrid
          sx={{
            height: '47rem',
            marginTop: '1rem',
            border: 'none',
            '.MuiDataGrid-columnHeaders div:first-of-type': {
              bgcolor: 'secondary.main',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '8px 8px 0 0',
            },
            '.MuiDataGrid-cell': {
              fontWeight: 500,
            },
            '.MuiDataGrid-sortIcon': { color: 'white' },
          }}
          rows={exceptions}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: query.limit,
              },
            },
            columns: {
              columnVisibilityModel: { id: false },
            },
          }}
          onPaginationModelChange={(model: GridPaginationModel) => setPagination(model)}
          onCellClick={(_params, event) => {
            if (
              openDialog ||
              openAddTicketDialog ||
              openBulkIgnoreDialog ||
              openBulkAssignTicketDialog ||
              openNewTicketDialog
            ) {
              event.defaultMuiPrevented = true
            }
          }}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          rowSelectionModel={selectedIds}
          checkboxSelection={selectedTab <= 1} // Only show checkboxes in New tab / In progress
          pageSizeOptions={[25, 50, 100]}
          paginationMode="server"
          rowCount={exceptions?.length}
          loading={isFetching}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          localeText={localeTextDatagrid}
          slots={{
            footer: () => (
              <Footer
                disabledPreview={query.offset === 0}
                disabledNext={exceptions?.length < query.limit}
                pagination={pagination}
                setPagination={setPagination}
              />
            ),
          }}
        />
      </Box>
      <ExceptionsFiltersDialog
        open={openFilter}
        applicationNames={applicationNames}
        isFetching={isFetchingAppNames}
        filterValues={query}
        onApplyFilters={handleFilters}
        onCancel={() => setOpenFilter(false)}
        handleClearFilter={handleClearFilter}
      />
      <EditExceptionDialog
        exceptionLog={selectedRow}
        exceptionStatuses={exceptionStatuses}
        open={openDialog}
        onSuccess={handleDialogSuccess}
        onCancel={() => {
          setOpenDialog(false)
          setSelectedRow(null)
        }}
      />
      <AddTicketDialog
        exceptionLog={selectedRow}
        open={openAddTicketDialog}
        onSuccess={handleDialogSuccess}
        onCancel={() => {
          setOpenAddTicketDialog(false)
          setSelectedRow(null)
        }}
      />
      <BulkIgnoreDialog
        selectedIds={selectedIdsArray}
        open={openBulkIgnoreDialog}
        onSuccess={handleBulkIgnoreSuccess}
        onCancel={() => {
          setOpenBulkIgnoreDialog(false)
        }}
      />
      <BulkAssignTicketDialog
        selectedIds={selectedIdsArray}
        open={openBulkAssignTicketDialog}
        onSuccess={handleBulkAssignTicketSuccess}
        onCancel={() => {
          setOpenBulkAssignTicketDialog(false)
        }}
      />
      <NewTicketDialog
        selectedIds={selectedIdsArray}
        selectedExceptions={selectedExceptions}
        severityValues={severityValues}
        plateformeValues={plateformeValues}
        open={openNewTicketDialog}
        onSuccess={handleNewTicketSuccess}
        onCancel={() => {
          setOpenNewTicketDialog(false)
        }}
      />
      <ToastComponent />
    </>
  )
}

export default BrowseExceptionsPage
